import React, { useId, useRef } from 'react';
import WikiIcon from '../shared/WikiIcon';
import { WikiField, WikiModalError } from './WikiModalShell';
import { WIKI_MODAL_COPY } from './wikiModalCopy';
import {
    WIKI_IMPORT_ACCEPT,
    formatImportSize,
    importMoreLabel,
    importPreviewRows,
    importStatsLabel,
} from './wikiImport';

/**
 * The "Import a file" body of the New page modal, between the mode switch and
 * LOCATION (C2 §1.5):
 *   no file / error   dashed chooser tile (click to browse — the prototype has
 *                     no drag-and-drop) · "No file handy? Try a sample" · info
 *                     box · [red error box]
 *   file loaded       file card ("{size} · {stats}", Replace) · PAGE TITLE ·
 *                     PREVIEW (first 7 blocks + "and N more") · [warnings]
 *
 * Deviations: the chooser is a <button> driving a hidden file input (keyboard
 * reachable; the prototype used a <label>); Replace clears AND reopens the OS
 * file dialog (prototype only cleared); a .docx shows its file card with
 * "Reading the file…" while the server converts it; PAGE TITLE can be cleared
 * (it then shows the detected title as a placeholder and falls back to it on
 * import) instead of snapping back.
 */
export default function WikiImportPanel({ importer, titleValue, titlePlaceholder, onTitleChange }) {
    const inputRef = useRef(null);
    const titleId = useId();
    const { imp } = importer;

    const openChooser = () => {
        const input = inputRef.current;
        if (!input) return;
        input.value = '';
        input.click();
    };

    const onFile = (event) => {
        const file = event.target.files && event.target.files[0];
        if (file) importer.load(file);
    };

    const replace = () => {
        importer.clear();
        openChooser();
    };

    const fileInput = (
        <input
            ref={inputRef}
            type="file"
            accept={WIKI_IMPORT_ACCEPT}
            onChange={onFile}
            hidden
            tabIndex={-1}
            data-testid="wiki-import-input"
        />
    );

    // The hidden input stays at ONE tree position in both states: Replace
    // opens the OS dialog on it and then swaps the file card for the chooser,
    // so an input that moved would be detached before its change event fired.
    const hasFile = Boolean(imp && !imp.error && (imp.pending || Array.isArray(imp.blocks)));
    if (!hasFile) {
        return (
            <>
                {fileInput}
                <button type="button" className="wk-imp-chooser" onClick={openChooser} data-wk-autofocus data-testid="wiki-import-chooser">
                    <span className="wk-imp-tile"><WikiIcon name="upload" size={19} /></span>
                    <span className="wk-t-name">{WIKI_MODAL_COPY.chooseTitle}</span>
                    <span className="wk-t-meta">{WIKI_MODAL_COPY.chooseTypes}</span>
                </button>
                <div className="wk-imp-sample wk-t-meta">
                    {WIKI_MODAL_COPY.sampleLead}
                    <button type="button" className="wk-imp-link" onClick={importer.loadSample} data-testid="wiki-import-sample">
                        {WIKI_MODAL_COPY.sampleLink}
                    </button>
                </div>
                <div className="wk-imp-info">{WIKI_MODAL_COPY.importInfo}</div>
                {imp && imp.error && <WikiModalError message={imp.error} testId="wiki-import-error" />}
            </>
        );
    }

    const parsed = Array.isArray(imp.blocks);
    const size = formatImportSize(imp.size);
    const sub = parsed
        ? [size, importStatsLabel(imp.blocks)].filter(Boolean).join(' · ')
        : [size, WIKI_MODAL_COPY.reading].filter(Boolean).join(' · ');
    const rows = parsed ? importPreviewRows(imp.blocks) : [];
    const more = parsed ? importMoreLabel(imp.blocks) : '';
    const warnings = parsed ? imp.warnings || [] : [];

    return (
        <>
            {fileInput}
            <div className="wk-imp-file" data-testid="wiki-import-file">
                <span className="wk-imp-tile wk-imp-tile--sm"><WikiIcon name="file-text" size={16} /></span>
                <div className="wk-imp-file-main">
                    <div className="wk-imp-file-name wk-t-name">{imp.name}</div>
                    <div className="wk-t-meta" data-testid="wiki-import-stats">{sub}</div>
                </div>
                <button type="button" className="wk-imp-replace" onClick={replace} data-testid="wiki-import-replace">
                    {WIKI_MODAL_COPY.replace}
                </button>
            </div>
            {parsed && (
                <>
                    <WikiField label={WIKI_MODAL_COPY.pageTitleLabel} htmlFor={titleId}>
                        <input
                            id={titleId}
                            className="wk-modal-input"
                            value={titleValue}
                            placeholder={titlePlaceholder}
                            onChange={(event) => onTitleChange(event.target.value)}
                            maxLength={240}
                            data-field="wiki.page_title"
                            data-testid="wiki-import-title"
                        />
                    </WikiField>
                    <WikiField label={WIKI_MODAL_COPY.previewLabel} variant="tight">
                        <div className="wk-imp-preview" data-testid="wiki-import-preview">
                            {rows.map((row, index) => (
                                <div key={`${index}:${row.type}`} className="wk-imp-row">
                                    <span className="wk-imp-type wk-t-label">{row.type}</span>
                                    <span className="wk-imp-text">{row.text}</span>
                                </div>
                            ))}
                            {more && <div className="wk-imp-more wk-t-meta" data-testid="wiki-import-more">{more}</div>}
                        </div>
                    </WikiField>
                    {warnings.length > 0 && (
                        <div className="wk-imp-warn" data-testid="wiki-import-warnings">
                            {warnings.map((warning) => (
                                <div key={warning} className="wk-imp-warn-item">
                                    <WikiIcon name="info" size={13} />
                                    <span>{warning}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </>
    );
}
